import { useRef, useState } from "react";
import type { MutableRefObject } from "react";
import { t } from "@/i18n-js/instance";
import { reactQueryGet } from "@/react/helpers/backendRequestHelpers";
import { internalApi } from "@/react/helpers/urlHelpers";
import type { ChatRoomMessage } from "@/react/types/Chat/ChatRoomMessage";
import type { ChatRoomParticipant } from "@/react/types/Chat/ChatRoomParticipant";
import { DIRECT_CHAT_ROOM } from "@circle-react/components/constants";
import {
  useChatStore,
  useUnreadChatRoomUuids,
} from "@circle-react/hooks/chatsV2";
import { useElectronNotification } from "../notifications/useElectronNotification";
import { useChatRoomParticipant } from "./useChatRoomParticipant";

interface UseChatHeaderApiProps {
  communityId: string;
}

interface LatestChatRoomMessage {
  chat_room_uuid: string;
  chat_room_kind: string;
  sender: ChatRoomMessage["sender"];
  embedded: boolean;
  body: string;
  name: string;
}

interface EventData {
  event: "newMessage" | "chatRoomRead";
  json_message?: LatestChatRoomMessage;
  chat_room_uuid?: string;
}

interface UnreadChatRoomsData {
  chat_room_uuids: string[];
}

export const useChatHeaderApi = ({ communityId }: UseChatHeaderApiProps) => {
  const { setUnreadChatRoomUuids } = useChatStore();
  const { sendNotificationToDesktopApp } = useElectronNotification();
  const unreadChatRoomUuids = useUnreadChatRoomUuids({ communityId });
  const [newUnreadChatRoomBatch, setnewUnreadChatRoomBatch] = useState<
    string[]
  >([]);
  const [chatRoomUuid, setChatRoomUuid] = useState<string | null>(null);
  const unreadChatRoomUuidsRef = useRef(unreadChatRoomUuids);
  const newUnreadChatRoomBatchRef = useRef(newUnreadChatRoomBatch);

  const mutation = useChatRoomParticipant(chatRoomUuid);

  const onNewMessage = async (
    unreadChatRoomUuidsRefParam: MutableRefObject<string[]>,
    eventData: EventData,
    communityMemberId: number,
  ) => {
    const latestChatRoomMessage = eventData.json_message;
    if (!latestChatRoomMessage) return;

    const {
      chat_room_uuid,
      chat_room_kind,
      sender,
      embedded: isEmbedded,
    } = latestChatRoomMessage;
    const openChatRoomUuid = window.location.pathname?.split("messages/")[1];

    const { community_member_id } = sender;
    const isChatRoomOpen = openChatRoomUuid === chat_room_uuid;
    const isDirectChatRoom = chat_room_kind === DIRECT_CHAT_ROOM;

    // if the sender is not the current user, send a notification to desktop app
    if (community_member_id !== communityMemberId) {
      sendNotificationToDesktopApp({
        title: `${latestChatRoomMessage.sender.name} ${t("desktop_app.notifications.new_message_text")}`,
        body: latestChatRoomMessage.body,
        metadata: latestChatRoomMessage,
        type: "message",
      });
    }

    if (isEmbedded || isChatRoomOpen) {
      return;
    }

    setChatRoomUuid(chat_room_uuid);

    let currentChatRoomParticipant: ChatRoomParticipant | undefined | null;
    if (!isDirectChatRoom) {
      const result = await mutation.mutateAsync();
      currentChatRoomParticipant = result?.chat_room_participant;
    }

    //community_member_id: sender of the message
    //communityMemberId: current logged in community member
    if (
      community_member_id === Number(communityMemberId) ||
      currentChatRoomParticipant?.muted
    ) {
      return;
    }

    const messageChatRoom = unreadChatRoomUuidsRefParam?.current?.find(
      uuid => uuid === chat_room_uuid,
    );

    if (!messageChatRoom) {
      setnewUnreadChatRoomBatch([
        ...newUnreadChatRoomBatchRef.current,
        chat_room_uuid,
      ]);
      setTimeout(() => handleTimeoutOver(), 2000);
    }
  };

  const handleTimeoutOver = () => {
    setUnreadChatRoomUuids(Number(communityId), [
      ...new Set([
        ...unreadChatRoomUuidsRef.current,
        ...newUnreadChatRoomBatchRef.current,
      ]),
    ]);
    setnewUnreadChatRoomBatch([]); // Clear the batch after processing
  };

  const onChatRoomRead = (
    unreadChatRoomUuidsRefParam: MutableRefObject<string[]>,
    eventData: EventData,
  ) => {
    const { chat_room_uuid } = eventData;
    if (!chat_room_uuid) return;

    const updatedUnreadChatRoomUuidsRef =
      unreadChatRoomUuidsRefParam?.current?.filter(
        uuid => uuid !== chat_room_uuid,
      );

    const updatednewUnreadChatRoomBatch =
      newUnreadChatRoomBatchRef?.current?.filter(
        uuid => uuid !== chat_room_uuid,
      );

    setnewUnreadChatRoomBatch(updatednewUnreadChatRoomBatch);
    setUnreadChatRoomUuids(Number(communityId), updatedUnreadChatRoomUuidsRef);
  };

  const onEventReceive = (
    unreadChatRoomUuidsRefParam: MutableRefObject<string[]>,
    eventData: EventData,
    communityMemberId: number,
  ) => {
    switch (eventData.event) {
      case "newMessage":
        void onNewMessage(
          unreadChatRoomUuidsRefParam,
          eventData,
          communityMemberId,
        );
        break;
      case "chatRoomRead":
        onChatRoomRead(unreadChatRoomUuidsRefParam, eventData);
    }
  };

  const fetchUnreadChatRoomUuids = (communityIdParam: string) => {
    reactQueryGet<UnreadChatRoomsData>(
      internalApi.chatRooms.unreadChatRooms({
        params: {
          community_id: communityIdParam,
        },
      }),
    )
      .then(data => {
        if (data.chat_room_uuids) {
          setUnreadChatRoomUuids(
            Number(communityIdParam),
            data.chat_room_uuids,
          );
        }
      })
      .catch(error => {
        console.error(error);
      });
  };

  // Update refs whenever state changes
  unreadChatRoomUuidsRef.current = unreadChatRoomUuids;
  newUnreadChatRoomBatchRef.current = newUnreadChatRoomBatch;

  return {
    unreadChatRoomUuids,
    onEventReceive,
    fetchUnreadChatRoomUuids,
    unreadChatRoomUuidsRef,
    newUnreadChatRoomBatch,
    newUnreadChatRoomBatchRef,
    setUnreadChatRoomUuids,
  };
};
